import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../dppApi';

// The brand's inquiry queue (dashboard "Inquiries" tab). Unlike maintenance (a
// repair with pickup/dropoff), an inquiry is a request to talk — a consultation,
// customisation or question — and the merchant negotiates an APPOINTMENT to meet,
// in store or over a virtual call. The conversation lives in one timeline (chat
// order, oldest→newest); messaging and scheduling are separate acts. The appointment
// time only surfaces at the top once BOTH sides have agreed (status confirmed).
const meetingLabel = (t) => (t === 'virtual' ? 'Virtual call' : 'In-store visit');
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
const STATUS_LABELS = {
    pending: 'New', proposed: 'Time proposed', countered: 'Customer proposed time',
    confirmed: 'Confirmed', flagged: 'Accommodate customer', completed: 'Completed', cancelled: 'Cancelled',
};
const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d) ? iso : d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};
// How each history event's appointment time reads on the timeline.
const EVENT_VERB = { asked: 'requested', proposed: 'proposed', countered: 'responded with', confirmed: 'confirmed' };
const HISTORY_HEAD = 1;   // collapsed, only the most recent message shows; the rest fold behind the toggle

// The queue is grouped by where each inquiry is in its lifecycle. Order = the order
// the sections appear on the dashboard.
const INQUIRY_GROUPS = [
    { key: 'upcoming', title: 'Upcoming', statuses: ['confirmed'] },
    { key: 'scheduling', title: 'Awaiting confirmation', statuses: ['pending', 'proposed', 'countered', 'flagged'] },
    { key: 'completed', title: 'Completed', statuses: ['completed'], collapsible: true },
    { key: 'cancelled', title: 'Cancelled', statuses: ['cancelled'], collapsible: true },
];

const BrandInquiries = ({ token }) => {
    const [inquiries, setInquiries] = useState(null);
    const [error, setError] = useState(null);
    const [proposeFor, setProposeFor] = useState(null);   // { id, when, action } — time picker (propose | reschedule)
    // Terminal sections start collapsed — the merchant's attention is on the active ones.
    const [collapsed, setCollapsed] = useState({ completed: true, cancelled: true });
    const toggle = (key) => setCollapsed(c => ({ ...c, [key]: !c[key] }));
    const [replies, setReplies] = useState({});   // per-inquiry edited reply (defaults to the AI suggestion)
    const [times, setTimes] = useState({});       // per-inquiry proposed new time (bundled with Send)
    const [timeOpen, setTimeOpen] = useState({}); // per-inquiry: the "Propose New Time" field is showing
    const [historyOpen, setHistoryOpen] = useState({});   // per-inquiry: full history expanded

    const load = useCallback(async () => {
        try { setInquiries(await apiFetch('/api/brand/inquiries', { token })); setError(null); }
        catch (err) { setError(err.message); }
    }, [token]);

    // Poll + refetch on focus so a customer's proposal/counter/message shows up
    // without a manual refresh (the negotiation is asynchronous). Open composer/
    // picker state is separate, so a background refresh won't disturb it.
    useEffect(() => {
        load();
        const id = setInterval(load, 15000);
        const onFocus = () => load();
        window.addEventListener('focus', onFocus);
        return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
    }, [load]);

    const patch = async (id, body, opts = {}) => {
        try {
            await apiFetch(`/api/brand/inquiries/${id}`, { method: 'PATCH', token, body });
            if (!opts.keepForm) setProposeFor(null);   // a message send leaves an open time picker alone
            load();
        } catch (err) { setError(err.message); }
    };

    // The AI draft only auto-populates when the CUSTOMER spoke last (it's genuinely
    // the brand's turn to respond). Once the brand has replied/acted most recently,
    // there's nothing to respond to, so the box stays blank.
    const suggested = (r) => {
        const ev = r.events || [];
        const lastFromCustomer = ev.length && ev[ev.length - 1].actor === 'customer';
        return lastFromCustomer ? (r.suggested_reply || '') : '';
    };
    // The reply shown in the always-on composer: the merchant's edit if they've
    // touched it, else the pre-generated suggestion (only when it's their turn).
    const replyText = (r) => (replies[r.inquiry_id] ?? suggested(r));
    const onReplyChange = (id, v) => setReplies(m => ({ ...m, [id]: v }));
    const isDrafted = (r) => replies[r.inquiry_id] === undefined && !!suggested(r);

    // The new time bundled into the composer (only usable on a confirmed appt, when
    // the "Propose New Time" field is open with a value).
    const composerTime = (r) => ((r.status === 'confirmed' && timeOpen[r.inquiry_id]) ? (times[r.inquiry_id] || '').trim() : '');
    const composerCanSend = (r) => !!(replyText(r).trim() || composerTime(r));

    // One combined Send: if a new time is set it reschedules (re-opens agreement) and
    // carries the fresh message; otherwise it's a plain message. Clears the composer.
    const sendComposer = (r) => {
        const text = replyText(r).trim();
        const when = composerTime(r);
        if (!text && !when) return;
        setReplies(m => { const n = { ...m }; delete n[r.inquiry_id]; return n; });
        setTimes(m => { const n = { ...m }; delete n[r.inquiry_id]; return n; });
        setTimeOpen(m => { const n = { ...m }; delete n[r.inquiry_id]; return n; });
        if (when) patch(r.inquiry_id, { action: 'reschedule', appointment_time: when, merchant_reply: text });
        else patch(r.inquiry_id, { action: 'reply', merchant_reply: text }, { keepForm: true });
    };

    if (!inquiries && !error) return <p className="collection-status collection-status--flush">Loading…</p>;

    // Time picker — used both to propose (pending/countered) and to reschedule a
    // confirmed appointment (which re-opens the agreement). No message rides along.
    const timeForm = (r) => {
        const isResched = proposeFor?.action === 'reschedule';
        return (
            <div className="inquiry-propose">
                <label className="dpp-transfer-label">{isResched ? 'Reschedule — propose a new time' : 'Propose an appointment time'}</label>
                <input className="dpp-input" type="datetime-local"
                    value={proposeFor?.when || ''}
                    onChange={e => setProposeFor(p => ({ ...p, when: e.target.value }))} />
                <div className="maintenance-actions">
                    <button className="catalogue-btn-primary" disabled={!proposeFor?.when}
                        onClick={() => patch(r.inquiry_id, { action: proposeFor.action || 'propose', appointment_time: proposeFor.when })}>Send proposal</button>
                    <button className="catalogue-btn-secondary" onClick={() => setProposeFor(null)}>Cancel</button>
                </div>
            </div>
        );
    };

    const renderInquiry = (r) => {
        const initial = (r.product || '?').trim().charAt(0).toUpperCase();
        const picking = proposeFor?.id === r.inquiry_id;   // time picker open for this card
        const terminal = r.status === 'completed' || r.status === 'cancelled';
        const upcoming = r.appointment_time && new Date(r.appointment_time) > new Date();
        const events = r.events || [];   // chat order: oldest → newest (as stored)
        const isExp = !!historyOpen[r.inquiry_id];
        const hidden = Math.max(0, events.length - HISTORY_HEAD);
        const visible = (isExp || hidden === 0) ? events : events.slice(-HISTORY_HEAD);   // most-recent tail
        return (
            <div className="dpp-card maintenance-card" key={r.inquiry_id}>
                <div className="maintenance-main">
                    <div className="maintenance-thumb">
                        {r.image
                            ? <img src={r.image} alt={r.product || 'item'} />
                            : <span className="maintenance-thumb-fallback">{initial}</span>}
                    </div>
                    <div className="maintenance-body">
                        <div className="maintenance-head">
                            <span className="maintenance-piece">{r.product || '—'}</span>
                            <span className={`maintenance-status maintenance-status--${r.status}`}>{STATUS_LABELS[r.status] || cap(r.status)}</span>
                        </div>
                        <p className="maintenance-sub">
                            {r.requester || 'Customer'} · {meetingLabel(r.meeting_type)} · via {cap((r.source || '').replace('_', ' '))}
                        </p>
                        {/* The agreed time surfaces at the top ONLY once confirmed. */}
                        {r.status === 'confirmed' && r.appointment_time && (
                            <p className="maintenance-sub inquiry-appt-confirmed">{fmt(r.appointment_time)}</p>
                        )}
                    </div>
                </div>

                {/* One continuous timeline (chat order). Collapsed shows the latest; the
                    toggle at the top reveals the earlier history above it. */}
                {events.length > 0 && (
                    <div className="inquiry-timeline">
                        {hidden > 0 && (
                            <button type="button" className="inquiry-tl-toggle"
                                onClick={() => setHistoryOpen(m => ({ ...m, [r.inquiry_id]: !isExp }))}>
                                {isExp ? 'Hide conversation' : `Show conversation (${hidden})`}
                            </button>
                        )}
                        {visible.map((e, i) => {
                            const you = e.actor === 'merchant';
                            const who = you ? 'You' : (r.requester || 'Customer');
                            const dot = you ? 'grey' : 'black';   // you = grey, other party = black
                            const verb = EVENT_VERB[e.kind];
                            return (
                                <div className="inquiry-ev" key={`${e.created_at}-${e.kind}-${i}`}>
                                    <span className={`inquiry-ev-dot inquiry-ev-dot--${dot}`} />
                                    <div className="inquiry-ev-body">
                                        <div className="inquiry-ev-top">
                                            <span className="inquiry-ev-who">{who}</span>
                                            <span className="inquiry-ev-time">{fmt(e.created_at)}</span>
                                        </div>
                                        {e.body && <div className="inquiry-ev-text">“{e.body}”</div>}
                                        {e.kind === 'completed' && <div className="inquiry-ev-note">Marked completed</div>}
                                        {e.kind === 'cancelled' && <div className="inquiry-ev-note">Cancelled</div>}
                                        {e.appointment_time && verb && (
                                            <span className="inquiry-ev-appt">{verb} · {fmt(e.appointment_time)}</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Always-on reply composer, docked below the timeline — stays visible
                    even while a time picker is open, so a half-typed message is never
                    swallowed. Sending is its own act (never tied to a time). */}
                {!terminal && (
                    <div className="inquiry-reply-dock">
                        {isDrafted(r) && <p className="inquiry-reply-caption">Suggested reply · edit before sending</p>}
                        <textarea className="dpp-input inquiry-reply-text" placeholder="Message the customer…"
                            value={replyText(r)}
                            onChange={e => onReplyChange(r.inquiry_id, e.target.value)} />
                        {/* Reschedule, bundled into the composer: propose a new time and send it
                            with the message in a single Send. Only on a confirmed, upcoming appt. */}
                        {r.status === 'confirmed' && upcoming && timeOpen[r.inquiry_id] && (
                            <input className="dpp-input inquiry-reply-time" type="datetime-local"
                                value={times[r.inquiry_id] || ''}
                                onChange={e => setTimes(m => ({ ...m, [r.inquiry_id]: e.target.value }))} />
                        )}
                        <div className="inquiry-reply-send">
                            {r.status === 'confirmed' && upcoming && (
                                <button type="button" className="catalogue-optional-toggle inquiry-proposetime"
                                    onClick={() => setTimeOpen(m => ({ ...m, [r.inquiry_id]: !m[r.inquiry_id] }))}>
                                    {timeOpen[r.inquiry_id] ? '– Propose New Time' : '+ Propose New Time'}
                                </button>
                            )}
                            <button className="catalogue-btn-primary" disabled={!composerCanSend(r)}
                                onClick={() => sendComposer(r)}>Send</button>
                        </div>
                    </div>
                )}

                {/* Scheduling actions — separate from messaging. */}
                {picking ? timeForm(r) : (
                    <>
                        {r.status === 'pending' && (
                            <div className="maintenance-actions">
                                <button className="catalogue-btn-primary" onClick={() => setProposeFor({ id: r.inquiry_id, when: '', action: 'propose' })}>Propose a time</button>
                            </div>
                        )}

                        {r.status === 'countered' && !r.at_cap && (
                            <div className="maintenance-actions">
                                <button className="catalogue-btn-primary" onClick={() => patch(r.inquiry_id, { action: 'accept' })}>Accept {fmt(r.appointment_time)}</button>
                                <button className="catalogue-btn-secondary" onClick={() => setProposeFor({ id: r.inquiry_id, when: '', action: 'propose' })}>Propose another time</button>
                                <button className="catalogue-btn-secondary" onClick={() => patch(r.inquiry_id, { action: 'cancel' })}>Cancel</button>
                            </div>
                        )}

                        {(r.status === 'flagged' || (r.status === 'countered' && r.at_cap)) && (
                            <div className="maintenance-actions">
                                <p className="inquiry-flag-note">You've used your proposals — please accommodate the customer's time.</p>
                                <button className="catalogue-btn-primary" onClick={() => patch(r.inquiry_id, { action: 'accept' })}>Accept {fmt(r.appointment_time)}</button>
                                <button className="catalogue-btn-secondary" onClick={() => patch(r.inquiry_id, { action: 'cancel' })}>Cancel</button>
                            </div>
                        )}

                        {r.status === 'proposed' && (
                            <div className="maintenance-actions">
                                <span className="inquiry-await">Pending customer response</span>
                                <button className="catalogue-btn-secondary" onClick={() => patch(r.inquiry_id, { action: 'cancel' })}>Cancel</button>
                            </div>
                        )}

                        {r.status === 'confirmed' && (
                            <div className="maintenance-actions">
                                <button className="catalogue-btn-primary" onClick={() => patch(r.inquiry_id, { action: 'complete' })}>Mark completed</button>
                                <button className="catalogue-btn-secondary" onClick={() => patch(r.inquiry_id, { action: 'cancel' })}>Cancel</button>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="maintenance-queue">
            {error && <p className="dpp-error">{error}</p>}
            {inquiries && !inquiries.length && (
                <p className="collection-status collection-status--flush">No inquiries yet.</p>
            )}
            {INQUIRY_GROUPS.map(g => {
                const items = (inquiries || []).filter(r => g.statuses.includes(r.status));
                if (!items.length) return null;
                const isCollapsed = g.collapsible && collapsed[g.key];
                return (
                    <section className="inquiry-group" key={g.key}>
                        {g.collapsible ? (
                            <button type="button" className="inquiry-group-title inquiry-group-toggle" onClick={() => toggle(g.key)} aria-expanded={!isCollapsed}>
                                <span className={`inquiry-group-chevron${isCollapsed ? '' : ' is-open'}`} aria-hidden="true">
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
                                </span>
                                <span>{g.title}</span><span className="inquiry-group-count">{items.length}</span>
                            </button>
                        ) : (
                            <h3 className="inquiry-group-title"><span>{g.title}</span><span className="inquiry-group-count">{items.length}</span></h3>
                        )}
                        {!isCollapsed && items.map(renderInquiry)}
                    </section>
                );
            })}
        </div>
    );
};

export default BrandInquiries;
